import { useMemo, useState } from 'react';
import { TRACKS } from '../../constants/appConstants.js';
import { recommendCert } from '../../constants/certRecommendation.js';
import { completeOnboarding } from '../../services/supabaseClient.js';
import { track } from '../../utils/track.js';

// ─────────────────────────────────────────────────────────────────
// PersonalizationFlow — the 7-step, employment-anchored, vision-first
// onboarding. Launched from the Roadmap "Personalize your path" card (NOT a
// gated signup takeover). Name/email/phone are collected at signup; this flow
// only collects personalization answers.
//
//   1 — Age range            (age_range)                         required
//   2 — What's driving you   (goal_type / intent)                required
//   3 — Role goal            (career_goal)                       required
//   4 — Experience baseline  (experience + confidence_baseline)  required
//   5 — Cert recommendation  (active_track_id / current_track)   required (confirm)
//   6 — Target date          (target_date + urgency_band)        skippable
//   7 — Study capacity       (study_capacity + study_hours)      skippable
//
// Experience (4) precedes the recommendation (5) because it feeds it: a complete
// beginner gets the A+ foundation first, while experienced learners start
// directly on the goal-matched track.
//
// Single final upsert: nothing persists until the final submit (abandon-before-
// submit writes nothing). completeOnboarding stamps onboarding_completed_at,
// which both the Dashboard and Roadmap nudge cards read as "personalized."
// ─────────────────────────────────────────────────────────────────

const TOTAL_STEPS = 7;

const AMBER   = 'var(--brand-amber)';
const EMERALD = 'var(--brand-emerald)';
const SLATE   = 'var(--brand-slate)';
const INK     = '#1e1e2e';

const AGE_OPTIONS = [
  { value: 'under_18',        label: 'Under 18' },
  { value: '18_24',          label: '18–24' },
  { value: '25_34',          label: '25–34' },
  { value: '35_44',          label: '35–44' },
  { value: '45_plus',        label: '45+' },
  { value: 'prefer_not_say', label: 'Prefer not to say' },
];

const INTENT_OPTIONS = [
  { value: 'first_it_job',  label: 'Landing my first IT job',          hint: 'New to the field and breaking in' },
  { value: 'career_switch', label: 'Switching careers into tech',      hint: 'Moving over from another industry' },
  { value: 'advancement',   label: 'Advancing in my current IT role',  hint: 'Level up where I already work' },
  { value: 'laid_off',      label: 'Recently laid off / between jobs',  hint: 'Getting back to work, fast' },
  { value: 'student',       label: 'Student / still exploring',         hint: 'Figuring out the right direction' },
];

// Each role goal carries a `key` (drives recommendCert + the cert chain) and a
// human-readable `label` (what we persist to career_goal, used as the display
// title across Roadmap / Career Hub / TrackSelector).
const ROLE_OPTIONS = [
  { key: 'help_desk',        label: 'Help Desk / IT Support' },
  { key: 'desktop_support',  label: 'Desktop Support' },
  { key: 'sysadmin',         label: 'Systems Administrator' },
  { key: 'network_engineer', label: 'Network Engineer' },
  { key: 'cloud_engineer',   label: 'Cloud Engineer' },
  { key: 'security_analyst', label: 'Security Analyst' },
  { key: 'it_manager',       label: 'IT Manager' },
  { key: 'modern_workplace', label: 'Microsoft 365 / Modern Workplace' },
  { key: 'unsure',           label: 'Not sure yet — help me decide' },
];

const EXPERIENCE_OPTIONS = [
  { value: 'beginner',     label: 'Complete beginner',   hint: 'Little to no IT background' },
  { value: 'intermediate', label: 'Some experience',     hint: 'Comfortable with the basics' },
  { value: 'advanced',     label: 'Already working in IT', hint: 'On the job and certifying up' },
];

const CONFIDENCE_OPTIONS = [
  { value: 'low',    label: 'Not very confident' },
  { value: 'medium', label: 'Somewhat confident' },
  { value: 'high',   label: 'Very confident' },
];

const TARGET_OPTIONS = [
  { value: '1',  label: 'About 1 month',  urgency: 'high',   months: 1 },
  { value: '3',  label: 'About 3 months', urgency: 'medium', months: 3 },
  { value: '6',  label: 'About 6 months', urgency: 'low',    months: 6 },
  { value: 'none', label: 'No specific date — at my own pace', urgency: null, months: null },
];

const CAPACITY_OPTIONS = [
  { value: 'half_to_one',  label: '30 min to an hour / day', hours: 1 },
  { value: 'one_to_three', label: '1–3 hours per day',       hours: 2 },
  { value: 'unsure',       label: 'Not sure yet',            hours: null },
];

// Experience + confidence → an initial overwhelm-risk estimate (0–1). Beginners
// with low confidence start at the highest risk; experienced/confident learners
// the lowest. Consumed downstream by the experience/motivation engines.
function computeOverwhelmRisk(experience, confidence) {
  const base = experience === 'beginner' ? 0.6 : experience === 'intermediate' ? 0.35 : 0.15;
  const adj  = confidence === 'low' ? 0.2 : confidence === 'high' ? -0.15 : 0;
  return Math.round(Math.max(0, Math.min(1, base + adj)) * 100) / 100;
}

function addMonthsISODate(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD — target_date is a `date` column
}

// ── Shared option-card grid ────────────────────────────────────
function OptionCard({ label, hint, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3,
        textAlign: 'left', width: '100%', padding: '15px 17px', borderRadius: 12,
        border: `1.5px solid ${selected ? AMBER : '#e5e7eb'}`,
        background: selected ? '#fffaf0' : '#fff',
        boxShadow: selected ? '0 4px 14px rgba(217,119,6,0.16)' : 'var(--card-shadow)',
        cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.12s',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = '#cbd2dc'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = '#e5e7eb'; }}
    >
      <span style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{label}</span>
      {hint && <span style={{ fontSize: 12, color: SLATE, lineHeight: 1.4 }}>{hint}</span>}
    </button>
  );
}

function OptionGrid({ children, cols = 2 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 10 }}>
      {children}
    </div>
  );
}

export default function PersonalizationFlow({ learner, onLearnerUpdate, onStartLesson, onExit }) {
  const [step, setStep]   = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [done, setDone]     = useState(false);

  // Local answer bag — nothing persists until the final submit.
  const [ageRange,   setAgeRange]   = useState(null);
  const [intent,     setIntent]     = useState(null);
  const [role,       setRole]       = useState(null);   // { key, label }
  const [experience, setExperience] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [targetSel,  setTargetSel]  = useState(null);   // TARGET_OPTIONS value
  const [capacity,   setCapacity]   = useState(null);   // CAPACITY_OPTIONS value

  const rec = useMemo(
    () => (role && experience ? recommendCert(role.key, experience) : null),
    [role, experience],
  );

  // Required-to-advance gate per step.
  const canAdvance = (
    step === 1 ? !!ageRange
    : step === 2 ? !!intent
    : step === 3 ? !!role
    : step === 4 ? (!!experience && !!confidence)
    : step === 5 ? !!rec
    : true   // 6 & 7 are skippable
  );

  const goBack = () => { setError(''); if (step === 1) onExit?.(); else setStep(s => s - 1); };
  const goNext = () => { setError(''); if (step < TOTAL_STEPS) setStep(s => s + 1); else submit(); };

  const submit = async () => {
    if (!learner?.id) { setError('Your session expired — please refresh and sign in again.'); return; }
    setSaving(true);
    setError('');

    const targetOpt   = TARGET_OPTIONS.find(t => t.value === targetSel);
    const capacityOpt = CAPACITY_OPTIONS.find(c => c.value === capacity);

    const fields = {
      age_range:              ageRange,
      goal_type:              intent,                 // repurposed signup_intent
      career_goal:            role.label,             // human-readable display title
      current_track:          rec.activeTrackId,      // always comptia_aplus today
      active_track_id:        rec.activeTrackId,
      cert_path:              rec.chain,               // recommended cert ladder → drives the Roadmap certification path
      experience,
      confidence_baseline:    confidence,
      overwhelm_risk_initial: computeOverwhelmRisk(experience, confidence),
    };
    // Skippable steps — only write when answered (else DB defaults / nulls hold).
    if (targetOpt && targetOpt.months != null) {
      fields.target_date  = addMonthsISODate(targetOpt.months);
      fields.urgency_band = targetOpt.urgency;
    }
    if (capacityOpt) {
      fields.study_capacity = capacityOpt.value;
      // "Not sure yet" has no hours — leave study_hours at its DB default.
      if (capacityOpt.hours != null) fields.study_hours = capacityOpt.hours;
    }

    const { error: err } = await completeOnboarding(learner.id, fields);
    setSaving(false);
    if (err) { setError('Could not save your setup. Please try again.'); return; }

    track('onboarding_completed', { intent, career_goal: role.label, track: rec.activeTrackId });
    // Reflect the new state up-tree so both nudge cards disappear and the
    // Roadmap re-renders personalized — onboarding_completed_at included.
    onLearnerUpdate?.({ ...fields, onboarding_completed_at: new Date().toISOString() });
    setDone(true);
  };

  // ── Completion screen ─────────────────────────────────────────
  if (done) {
    const startLabel = TRACKS[rec.activeTrackId]?.label || 'CompTIA A+';
    return (
      <Overlay>
        <div style={{ ...cardWrap, textAlign: 'center' }}>
          <span style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(5,150,105,0.12)', color: EMERALD, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </span>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: INK, margin: '0 0 8px' }}>Your path is set</h2>
          <p style={{ fontSize: 14.5, color: SLATE, margin: '0 auto 22px', maxWidth: 460, lineHeight: 1.6 }}>
            We've tailored your roadmap toward <strong style={{ color: INK }}>{role.label}</strong>, starting with{' '}
            <strong style={{ color: INK }}>{startLabel}</strong>. Give us 2–3 minutes to show you how AdaptOS helps you
            get certified faster — it'll teach one concept, check what you understood, and remember what to repair next.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => onStartLesson?.()} style={primaryBtn}>
              Start my first lesson →
            </button>
            <button type="button" onClick={() => onExit?.()} style={ghostBtn}>
              View my roadmap
            </button>
          </div>
        </div>
      </Overlay>
    );
  }

  const meta = STEP_META[step];

  return (
    <Overlay>
      <div style={cardWrap}>
        {/* Progress */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: SLATE }}>
              Step {step} of {TOTAL_STEPS}
            </span>
            {meta.skippable && (
              <button type="button" onClick={goNext} style={{ background: 'none', border: 'none', color: SLATE, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Skip for now →
              </button>
            )}
          </div>
          <div style={{ height: 6, background: '#eef0f4', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(step / TOTAL_STEPS) * 100}%`, background: AMBER, borderRadius: 99, transition: 'width 0.25s' }} />
          </div>
        </div>

        <h2 style={{ fontSize: 22, fontWeight: 800, color: INK, margin: '0 0 6px', lineHeight: 1.25 }}>{meta.title}</h2>
        {meta.subtitle && <p style={{ fontSize: 13.5, color: SLATE, margin: '0 0 20px', lineHeight: 1.55 }}>{meta.subtitle}</p>}

        {/* ── Step bodies ─────────────────────────────────────── */}
        {step === 1 && (
          <OptionGrid cols={3}>
            {AGE_OPTIONS.map(o => (
              <OptionCard key={o.value} label={o.label} selected={ageRange === o.value} onClick={() => setAgeRange(o.value)} />
            ))}
          </OptionGrid>
        )}

        {step === 2 && (
          <OptionGrid cols={1}>
            {INTENT_OPTIONS.map(o => (
              <OptionCard key={o.value} label={o.label} hint={o.hint} selected={intent === o.value} onClick={() => setIntent(o.value)} />
            ))}
          </OptionGrid>
        )}

        {step === 3 && (
          <OptionGrid cols={2}>
            {ROLE_OPTIONS.map(o => (
              <OptionCard key={o.key} label={o.label} selected={role?.key === o.key} onClick={() => setRole(o)} />
            ))}
          </OptionGrid>
        )}

        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div>
              <p style={subLabel}>Your IT experience</p>
              <OptionGrid cols={1}>
                {EXPERIENCE_OPTIONS.map(o => (
                  <OptionCard key={o.value} label={o.label} hint={o.hint} selected={experience === o.value} onClick={() => setExperience(o.value)} />
                ))}
              </OptionGrid>
            </div>
            <div>
              <p style={subLabel}>How confident do you feel starting out?</p>
              <OptionGrid cols={3}>
                {CONFIDENCE_OPTIONS.map(o => (
                  <OptionCard key={o.value} label={o.label} selected={confidence === o.value} onClick={() => setConfidence(o.value)} />
                ))}
              </OptionGrid>
            </div>
          </div>
        )}

        {step === 5 && rec && (
          <CertRecommendation rec={rec} roleLabel={role.label} />
        )}

        {step === 6 && (
          <OptionGrid cols={1}>
            {TARGET_OPTIONS.map(o => (
              <OptionCard key={o.value} label={o.label} selected={targetSel === o.value} onClick={() => setTargetSel(o.value)} />
            ))}
          </OptionGrid>
        )}

        {step === 7 && (
          <OptionGrid cols={1}>
            {CAPACITY_OPTIONS.map(o => (
              <OptionCard key={o.value} label={o.label} selected={capacity === o.value} onClick={() => setCapacity(o.value)} />
            ))}
          </OptionGrid>
        )}

        {error && (
          <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.22)', color: '#dc2626', fontSize: 13, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        {/* Footer nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 26 }}>
          <button type="button" onClick={goBack} disabled={saving} style={ghostBtn}>
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          <button type="button" onClick={goNext} disabled={!canAdvance || saving}
            style={{ ...primaryBtn, ...((!canAdvance || saving) ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
            {saving ? 'Saving…' : step === TOTAL_STEPS ? 'Finish setup →' : 'Continue →'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

// ── Step 4 cert-recommendation visual ──────────────────────────
// A track is "coming soon" (no live curriculum) when it carries no `status`
// field — except comptia_aplus, which is the live entry track and has none.
function isComingSoon(id) {
  return id !== 'comptia_aplus' && !TRACKS[id]?.status;
}

function CertRecommendation({ rec, roleLabel }) {
  return (
    <div>
      <div style={{ padding: '16px 18px', borderRadius: 12, border: `1.5px solid ${AMBER}`, background: '#fffaf0', marginBottom: 16 }}>
        <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: AMBER, margin: 0 }}>Recommended start</p>
        <p style={{ fontSize: 18, fontWeight: 800, color: INK, margin: '6px 0 4px' }}>{TRACKS[rec.startCert]?.label || 'CompTIA A+'}</p>
        <p style={{ fontSize: 13, color: SLATE, margin: 0, lineHeight: 1.5 }}>
          {rec.summary
            ? <>The right path to <strong style={{ color: INK }}>{roleLabel}</strong>. {rec.summary}</>
            : <>The strongest starting point for <strong style={{ color: INK }}>{roleLabel}</strong>. You'll begin here and build toward your goal.</>}
        </p>
      </div>

      <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: SLATE, margin: '0 0 10px' }}>
        Why these certifications for {roleLabel}
      </p>
      <div style={{ borderLeft: `2px dashed ${AMBER}`, marginLeft: 6, paddingLeft: 0 }}>
        {rec.chain.map((id, i) => {
          const isStart = i === 0;
          const soon    = isComingSoon(id);
          const t = TRACKS[id];
          const tag   = isStart ? 'Start here' : soon ? 'Coming soon' : 'Next step';
          const accent = isStart ? AMBER : SLATE;
          const why   = rec.why?.[id];
          return (
            <div key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0' }}>
              <span style={{ width: 16, borderTop: `2px dashed ${accent}`, flexShrink: 0, marginTop: 20 }} />
              <div style={{ flex: 1, border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}`, borderRadius: 10, padding: '10px 13px', background: isStart ? '#fffaf0' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{t?.label || id}</span>
                  <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: accent, background: isStart ? '#fff' : '#f1f3f7', padding: '3px 8px', borderRadius: 99 }}>
                    {tag}
                  </span>
                </div>
                {why && (
                  <p style={{ fontSize: 12, color: SLATE, margin: '7px 0 0', lineHeight: 1.5 }}>{why}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Layout primitives ──────────────────────────────────────────
function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      overflowY: 'auto', padding: '40px 16px',
      fontFamily: "'Inter','Segoe UI',sans-serif",
    }}>
      {children}
    </div>
  );
}

const cardWrap = {
  width: '100%', maxWidth: 560, background: '#fff', borderRadius: 18,
  boxShadow: '0 24px 60px rgba(15,23,42,0.28)', padding: 'clamp(24px, 4vw, 36px)',
};

const subLabel = { fontSize: 12.5, fontWeight: 800, color: INK, margin: '0 0 10px' };

const primaryBtn = {
  padding: '12px 24px', borderRadius: 10, background: AMBER, border: 'none', color: '#fff',
  fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
  boxShadow: '0 4px 14px rgba(217,119,6,0.26)',
};

const ghostBtn = {
  padding: '12px 18px', borderRadius: 10, background: 'transparent', border: '1px solid #e5e7eb',
  color: SLATE, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};

const STEP_META = {
  1: { title: "First, how old are you?", subtitle: 'This helps M[Emory] pace and frame your lessons. One tap — and "Prefer not to say" is always fine.' },
  2: { title: "What's driving you right now?", subtitle: 'Your situation shapes the whole plan — the goal, the pace, and how we keep you moving.' },
  3: { title: 'Which role are you aiming for?', subtitle: "Pick the closest fit — we'll map a certification path to it. Not sure? We'll help you decide." },
  4: { title: 'Where are you starting from?', subtitle: 'An honest read lets us calibrate difficulty — and decide whether to start you on the fundamentals or jump straight to your goal cert.' },
  5: { title: 'Here’s your recommended path', subtitle: 'Based on your goal and experience, here’s the certification we recommend starting with — and where it leads.' },
  6: { title: 'Any target date in mind?', subtitle: 'Optional — a deadline lets us pace your roadmap. Skip it and you’ll go at your own pace.', skippable: true },
  7: { title: 'How much time can you give it?', subtitle: 'Optional — this tunes your finish-date estimate. You can change it anytime.', skippable: true },
};
